import { useState } from 'react';

export type PinnableItem = {
    value: string;
    label: string;
    isRefined: boolean;
};

/**
 * Snapshots which items are refined at the moment the panel opens, or when search clears (so a
 * store selected mid-search joins the pinned set, and one deselected mid-session drops out of
 * it), sorted alphabetically among themselves. Cleared while `searchText` is set, so search
 * results stay in their normal match order.
 */
export function usePinnedOrder<T extends PinnableItem>(
    items: T[],
    searchText: string,
    isOpen: boolean,
): string[] {
    const [pinnedOrder, setPinnedOrder] = useState<string[]>([]);
    const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
    const [prevSearchText, setPrevSearchText] = useState(searchText);

    const recomputePinnedOrder = () => {
        const selected = items.filter((item) => item.isRefined);
        setPinnedOrder(
            [...selected].sort((a, b) => a.label.localeCompare(b.label)).map((item) => item.value),
        );
    };

    if (isOpen !== prevIsOpen) {
        setPrevIsOpen(isOpen);
        if (isOpen) recomputePinnedOrder();
    }

    if (searchText !== prevSearchText) {
        setPrevSearchText(searchText);
        if (!searchText) recomputePinnedOrder();
    }

    return searchText ? [] : pinnedOrder;
}
